import React, { useEffect, useRef, useState } from "react";
import { Box, Button, List, ListItem, Stack, Typography } from "@mui/material";
import { DrawerSection } from "../../../Components/Common/DrawerLayout/DrawerLayout";
import { useTranslation } from "../../../context/Internationalization";
import { GET_RESOLVE_USERS } from "../../../graphql/userGraphql";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";

const ClubAdminsList = ({ idsKey }) => {
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const request = useRef({ axiosGraphQL, errorLog });
  request.current = { axiosGraphQL, errorLog };
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    loading: true,
    error: false,
    users: [],
  });

  useEffect(() => {
    let active = true;
    const ids = JSON.parse(idsKey);
    const loadAdmins = async () => {
      setState({ loading: true, error: false, users: [] });
      try {
        const response = await request.current.axiosGraphQL({
          query: GET_RESOLVE_USERS,
          variables: { ids },
        });
        if (!active) return;
        const users = response.data?.data?.resolveUserIds;
        if (!Array.isArray(users))
          throw new Error("Invalid club administrators response");
        const usersById = new Map(
          users.filter(Boolean).map((user) => [user._id, user])
        );
        setState({
          loading: false,
          error: false,
          users: ids.map((id) => usersById.get(id)).filter(Boolean),
        });
      } catch (error) {
        if (!active) return;
        setState({ loading: false, error: true, users: [] });
        request.current.errorLog(
          error,
          "ClubAdminsSection",
          "loadAdmins",
          "GET_RESOLVE_USERS"
        );
      }
    };

    if (ids.length) loadAdmins();
    else setState({ loading: false, error: false, users: [] });
    return () => {
      active = false;
    };
  }, [idsKey, attempt]);

  if (state.loading) {
    return (
      <Typography role="status" color="text.secondary" variant="body2">
        {t("loading")}
      </Typography>
    );
  }

  if (state.error) {
    return (
      <Stack spacing={1} alignItems="flex-start">
        <Typography role="alert" color="error.main" variant="body2">
          {t("club_admins_load_failed")}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setAttempt((value) => value + 1)}
        >
          {t("retry")}
        </Button>
      </Stack>
    );
  }

  if (!state.users.length) {
    return (
      <Typography role="status" color="text.secondary" variant="body2">
        {t("no_club_admins")}
      </Typography>
    );
  }

  return (
    <List disablePadding aria-label={t("club_admins")}>
      {state.users.map((user) => (
        <ListItem
          key={user._id}
          disableGutters
          sx={{
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            "&:first-of-type": { pt: 0 },
            "&:last-of-type": { pb: 0, borderBottom: 0 },
          }}
        >
          <Box sx={{ minWidth: 0, width: "100%", overflowWrap: "anywhere" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {[user.firstName, user.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() || t("club_admin")}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ columnGap: 2, rowGap: 0.25, flexWrap: "wrap", mt: 0.5 }}
            >
              {user.email && (
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              )}
              {user.mobile && (
                <Typography variant="body2" color="text.secondary">
                  {user.mobile}
                </Typography>
              )}
            </Stack>
          </Box>
        </ListItem>
      ))}
    </List>
  );
};

const ClubAdminsSection = ({ adminIds = [] }) => {
  const { t } = useTranslation();
  const idsKey = JSON.stringify([...new Set((adminIds || []).filter(Boolean))]);

  return (
    <DrawerSection
      title={t("club_admins")}
      description={t("club_admins_description")}
      titleId="club-admins-section-title"
    >
      <ClubAdminsList key={idsKey} idsKey={idsKey} />
    </DrawerSection>
  );
};

export default ClubAdminsSection;
